import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// ─── Renderer ──────────────────────────────────────────────────────────────
const container = document.getElementById('canvas-container');
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.toneMapping = THREE.NoToneMapping;
container.appendChild(renderer.domElement);

// ─── Scene ─────────────────────────────────────────────────────────────────
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xd0e8f0);

const camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.01, 100);
camera.position.set(0, 1.4, 3.2);

// 縦長画面では水平視野が狭くなるため、モデル全体が収まる距離まで引く。
function updateCameraDistance() {
  const narrowScreenScale = Math.min(1.8, Math.max(1, 0.75 / camera.aspect));
  camera.position.z = 3.2 * narrowScreenScale;
}
updateCameraDistance();

const controls = new OrbitControls(camera, renderer.domElement);
controls.target.set(0, 0.9, 0);

// ─── Post-processing: 目のemission glow ────────────────────────────────────
// scene/camera の後に初期化 (RenderPass が両方を必要とするため)
const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
// 閾値を高め(0.85)に設定 → 明るい目(R≈1.0)だけbloom、暗いボディは対象外
const bloomPass = new UnrealBloomPass(
  new THREE.Vector2(window.innerWidth, window.innerHeight),
  0.45,  // strength
  0.35,  // radius
  0.85,  // threshold
);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());
controls.enabled = false; // カメラ操作は一旦無効化

// ─── Toon Gradient Map ─────────────────────────────────────────────────────
const gradientData = new Uint8Array([
   30,  30,  30, 255,
  100, 100, 100, 255,
  200, 200, 200, 255,
  255, 255, 255, 255,
]);
const gradientMap = new THREE.DataTexture(gradientData, 4, 1, THREE.RGBAFormat);
gradientMap.minFilter = THREE.NearestFilter;
gradientMap.magFilter = THREE.NearestFilter;
gradientMap.needsUpdate = true;

// ─── Materials ─────────────────────────────────────────────────────────────
const MATERIAL_COLORS = {
  kenami: 0x4a4a5a,
  eye:    0xFFE040,  // Unlitで照明無視 + bloom triggerのため少し明るめ
  mouse:  0x5a2810,  // Unlitで暗め (bloomが乗らないよう閾値以下)
};
function makeToon(color) {
  return new THREE.MeshToonMaterial({ color, gradientMap });
}
// Unlit材質生成 (eye, mouseはライティング不要)
function makeUnlit(color) {
  return new THREE.MeshBasicMaterial({ color });
}

// ─── Lighting ──────────────────────────────────────────────────────────────
scene.add(new THREE.AmbientLight(0xaaaacc, 0.35));

const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
keyLight.position.set(1.5, 3.5, 2.5);
scene.add(keyLight);

const rimLight = new THREE.DirectionalLight(0xcc88ff, 0.9);
rimLight.position.set(-1, 2, -3);
scene.add(rimLight);

// ─── Ground ────────────────────────────────────────────────────────────────
const ground = new THREE.Mesh(
  new THREE.CircleGeometry(2.5, 64),
  new THREE.MeshToonMaterial({ color: 0xb8d4e8, gradientMap }),
);
ground.rotation.x = -Math.PI / 2;
scene.add(ground);

const ring = new THREE.Mesh(
  new THREE.RingGeometry(0.3, 0.85, 64),
  new THREE.MeshBasicMaterial({ color: 0xcc88ff, transparent: true, opacity: 0.15, side: THREE.DoubleSide }),
);
ring.rotation.x = -Math.PI / 2;
ring.position.y = 0.001;
scene.add(ring);

// ─── Particles ─────────────────────────────────────────────────────────────
const PARTICLE_COUNT = 60;
const pPos = new Float32Array(PARTICLE_COUNT * 3);
const pSpeeds = [];
for (let i = 0; i < PARTICLE_COUNT; i++) {
  const r = 1.0 + Math.random() * 1.4, a = Math.random() * Math.PI * 2;
  pPos[i*3] = Math.cos(a)*r; pPos[i*3+1] = Math.random()*2.5; pPos[i*3+2] = Math.sin(a)*r;
  pSpeeds.push(0.04 + Math.random() * 0.09);
}
const pGeo = new THREE.BufferGeometry();
pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
scene.add(new THREE.Points(pGeo, new THREE.PointsMaterial({
  color: 0xddaaff, size: 0.022, transparent: true, opacity: 0.5, sizeAttenuation: true,
})));

// ─── Spring Physics ────────────────────────────────────────────────────────
class BoneSpring {
  constructor(stiffness = 22, damping = 5.5) {
    this.k = stiffness;
    this.d = damping;
    this.rx = 0; this.rz = 0; this.ry = 0;
    this.vx = 0; this.vz = 0; this.vy = 0;
    this.tx = 0; this.tz = 0; this.ty = 0; // target (drag)
  }
  impulse(ix, iz, iy = 0) { this.vx += ix; this.vz += iz; this.vy += iy; }
  setTarget(rx, rz, ry = 0) { this.tx = rx; this.tz = rz; this.ty = ry; }
  clearTarget() { this.tx = 0; this.tz = 0; this.ty = 0; }
  update(dt) {
    const ax = -this.k * (this.rx - this.tx) - this.d * this.vx;
    const az = -this.k * (this.rz - this.tz) - this.d * this.vz;
    const ay = -this.k * (this.ry - this.ty) - this.d * this.vy;
    this.vx += ax * dt; this.vz += az * dt; this.vy += ay * dt;
    this.rx += this.vx * dt; this.rz += this.vz * dt; this.ry += this.vy * dt;
  }
}

// ─── Outline Helper ────────────────────────────────────────────────────────
function addOutline(mesh) {
  try {
    const mat = new THREE.MeshBasicMaterial({ color: 0x0a0808, side: THREE.BackSide });
    let outline;
    if (mesh.isSkinnedMesh) {
      // clone() を使うことで iOS Safari の readonly プロパティ問題を回避
      outline = mesh.clone();
      outline.material = mat;
    } else {
      outline = new THREE.Mesh(mesh.geometry, mat);
    }
    outline.scale.setScalar(1.012);
    outline.castShadow = false;
    outline.receiveShadow = false;
    mesh.parent.add(outline);
  } catch (e) {
    // アウトライン失敗はスキップ（モデル表示は継続）
    console.warn('outline skip:', mesh.name, e.message);
  }
}

// ─── Bone Helpers ──────────────────────────────────────────────────────────
function findBone(root, name) {
  let r = null;
  root.traverse(o => { if (o.isBone && o.name === name) r = o; });
  return r;
}

// ─── Poke Burst Particles ─────────────────────────────────────────────────
const burstPool = [];
function spawnBurst(point) {
  const count = 12;
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  const vel = [];
  for (let i = 0; i < count; i++) {
    pos[i*3] = point.x; pos[i*3+1] = point.y; pos[i*3+2] = point.z;
    vel.push(new THREE.Vector3(
      (Math.random()-0.5)*2,
      Math.random()*1.5,
      (Math.random()-0.5)*2,
    ).multiplyScalar(0.8 + Math.random()*0.5));
  }
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const pts = new THREE.Points(geo, new THREE.PointsMaterial({
    color: 0xffdd55, size: 0.04, transparent: true, opacity: 1, sizeAttenuation: true,
  }));
  scene.add(pts);
  burstPool.push({ pts, vel, life: 1.0 });
}
function updateBursts(dt) {
  for (let i = burstPool.length - 1; i >= 0; i--) {
    const b = burstPool[i];
    b.life -= dt * 2.0;
    if (b.life <= 0) { scene.remove(b.pts); b.pts.geometry.dispose(); burstPool.splice(i, 1); continue; }
    b.pts.material.opacity = b.life;
    const pos = b.pts.geometry.attributes.position;
    for (let j = 0; j < b.vel.length; j++) {
      b.vel[j].y -= 2.0 * dt;
      pos.array[j*3]   += b.vel[j].x * dt;
      pos.array[j*3+1] += b.vel[j].y * dt;
      pos.array[j*3+2] += b.vel[j].z * dt;
    }
    pos.needsUpdate = true;
  }
}

// ─── bounboun Animation (Unity .anim → JS hermite) ────────────────────────
const BB_DURATION = 0.8333333; // seconds (50 frames @ 60fps)

const DEG2RAD = Math.PI / 180;

// Hermite cubic spline interpolation (Unity AnimationCurve format)
// t must already be clamped to [kf[0].t, kf[last].t)
function hermiteInterp(kf, t) {
  let i = kf.length - 2;
  for (let j = 0; j < kf.length - 1; j++) { if (t < kf[j+1].t) { i = j; break; } }
  const k0 = kf[i], k1 = kf[i+1];
  const dt = k1.t - k0.t;
  const s  = (t - k0.t) / dt;
  const s2 = s*s, s3 = s2*s;
  return (2*s3 - 3*s2 + 1)*k0.v + (s3 - 2*s2 + s)*dt*k0.os
       + (-2*s3 + 3*s2)*k1.v   + (s3 - s2)*dt*k1.is;
}

// m_LocalPosition.y キーフレーム (path: "" = root)
const BB_POS_Y = [
  { t:0,          v:2.0,       os:0,          is:0 },
  { t:0.13333334, v:1.46,      os:-2.8235292, is:-2.8235292 },
  { t:0.28333333, v:1.2,       os:0,          is:0 },
  { t:0.36666667, v:1.3,       os:1.3499997,  is:1.3499997 },
  { t:0.41666666, v:1.38,      os:1.600001,   is:1.600001 },
  { t:0.56666666, v:1.9396832, os:0.5790413,  is:0.5790413 },
  { t:0.8333333,  v:2.0,       os:0,          is:0 },
];
// m_LocalScale.x (スカッシュ方向)
const BB_SCALE_X = [
  { t:0,          v:0.5,        os:0,          is:0 },
  { t:0.13333334, v:0.51,       os:0.14117634, is:0.14117634 },
  { t:0.28333333, v:0.58833236, os:0,          is:0 },
  { t:0.36666667, v:0.54,       os:-0.6624927, is:-0.6624927 },
  { t:0.41666666, v:0.5,        os:0,          is:0 },
  { t:0.56666666, v:0.5,        os:0,          is:0 },
  { t:0.8333333,  v:0.5,        os:0,          is:0 },
];
// m_LocalScale.y (ストレッチ方向)
const BB_SCALE_Y = [
  { t:0,          v:0.5,        os:0,          is:0 },
  { t:0.13333334, v:0.51343733, os:0,          is:0 },
  { t:0.28333333, v:0.38039196, os:0,          is:0 },
  { t:0.36666667, v:0.3974375,  os:0.34862283, is:0.34862283 },
  { t:0.41666666, v:0.426875,   os:0.47978494, is:0.47978494 },
  { t:0.56666666, v:0.4933945,  os:0.06341286, is:0.06341286 },
  { t:0.8333333,  v:0.5,        os:0,          is:0 },
];
// 表情切り替え: egao (><) は 0.133〜0.433 のあいだ visible
const BB_EGAO_START = 0.13333334;
const BB_EGAO_END   = 0.43333334;

// ─── futaShime クリップ (2s・スピンジャンプ) ──────────────────────────────
const FS_DURATION = 2.0;
const FS_POS_Y = [
  {t:0,         v:2.68,      os:0,         is:0},
  {t:1,         v:1.46,      os:-0.52173966, is:-0.52173966},
  {t:1.15,      v:1.31,      os:0,         is:0},
  {t:1.2333333, v:1.32,      os:0.3000033, is:0.3000033},
  {t:1.2833333, v:1.47,      os:3.0000007, is:3.0000007},
  {t:1.4333333, v:1.9396832, os:0.6814182, is:0.6814182},
  {t:1.45,      v:1.9680756, os:0.3586412, is:0.3586412},
  {t:1.75,      v:2.384867,  os:1.298683,  is:1.298683},
  {t:1.8333334, v:2.509324,  os:1.1805325, is:1.1805325},
  {t:2,         v:2.68,      os:0,         is:0},
];
const FS_SCALE_X = [
  {t:0,         v:0.5,        os:0,           is:0},
  {t:1,         v:0.51,       os:0.034782577, is:0.034782577},
  {t:1.15,      v:0.58833236, os:0,           is:0},
  {t:1.2333333, v:0.54,       os:-0.6624927,  is:-0.6624927},
  {t:1.2833333, v:0.5,        os:0,           is:0},
  {t:2,         v:0.5,        os:0,           is:0},
];
const FS_SCALE_Y = [
  {t:0,         v:0.5,        os:0,           is:0},
  {t:1,         v:0.51343733, os:0,           is:0},
  {t:1.15,      v:0.38039196, os:0,           is:0},
  {t:1.2333333, v:0.3974375,  os:0.34862283,  is:0.34862283},
  {t:1.2833333, v:0.426875,   os:0.47978508,  is:0.47978508},
  {t:1.4333333, v:0.4933945,  os:0.036867943, is:0.036867943},
  {t:2,         v:0.5,        os:0,           is:0},
];
// ルート X 回転 (degrees): 末尾でフロントスピン360°
const FS_ROT_X = [
  {t:0,         v:0,   os:0,        is:0},
  {t:1.4333333, v:0,   os:0,        is:0},
  {t:1.75,      v:180, os:674.9998, is:674.9998},
  {t:1.8333334, v:270, os:720,      is:720},
  {t:2,         v:360, os:0,        is:0},
];

// ─── utouto クリップ (4.8s・眠気に負ける待機ループ) ────────────────────
// 手続きアニメーションなので、ループ端では全ての値が同じ姿勢に戻る。
const UTOUTO_DURATION = 4.8;

let animTime = 0;
let currentAnim = 'utouto';

// ─── Load Model ────────────────────────────────────────────────────────────
const clock = new THREE.Clock();
let model = null;
const bones = {};
const springs = {};
let pokeableMeshes = [];
const egaoObjects   = []; // egao配下の全Object3D（Group含む）
const futsuuObjects = []; // futsuu配下の全Object3D

// 実際にnekobodyのスキンウェイトが乗っているボーンのみ
// お尻・お腹が最大ウェイト。胴・腰はウェイトゼロで動かしても無意味
const BONE_NAMES = ['お尻','お腹','首','頭','顔','耳.L','耳先.L','耳.R','耳先.R','しっぽ根本','しっぽ中','しっぽ先','肩.L','肩.R','手.L','手.R','足.L','足.R'];

const loader = new GLTFLoader();
loader.load('./nekoAnime.glb', (gltf) => {
  model = gltf.scene;

  // Step1: egao/futsuu 配下の全オブジェクト収集 & egao 初期非表示
  // (マルチマテリアル → GLTF複数プリミティブ → Three.js Group+複数SkinnedMesh 対応)
  model.traverse(o => {
    if (o.name === 'egao')   { egaoObjects.push(o);   o.visible = false; }
    if (o.name === 'futsuu') { futsuuObjects.push(o); }
  });

  // Step2: 各メッシュにマテリアル適用
  model.traverse(obj => {
    if (!obj.isMesh && !obj.isSkinnedMesh) return;

    // 祖先名でメッシュ種別を判定（Group経由の子メッシュに対応）
    let isEgaoMesh = false, isFutsuuMesh = false, isNekobody = false;
    let cur = obj;
    while (cur) {
      if (cur.name === 'egao')     { isEgaoMesh   = true; break; }
      if (cur.name === 'futsuu')   { isFutsuuMesh = true; break; }
      if (cur.name === 'nekobody') { isNekobody   = true; break; }
      cur = cur.parent;
    }
    const isFaceDecal = isEgaoMesh || isFutsuuMesh;

    const applyMat = (m) => {
      const isEyeMat   = m.name === 'eye';
      const isMouthMat = m.name === 'mouse';
      const mat = (isEyeMat || isMouthMat)
        ? makeUnlit(MATERIAL_COLORS[m.name] ?? 0xffffff)
        : makeToon(MATERIAL_COLORS[m.name] ?? 0xffffff);
      if (isNekobody) {
        mat.stencilWrite = true;
        mat.stencilRef   = 1;
        mat.stencilFunc  = THREE.AlwaysStencilFunc;
        mat.stencilZPass = THREE.ReplaceStencilOp;
      } else if (isFaceDecal) {
        mat.stencilRef          = 1;
        mat.stencilFunc         = THREE.EqualStencilFunc;
        mat.polygonOffset       = true;
        mat.polygonOffsetFactor = -1;
        mat.polygonOffsetUnits  = -2;
      }
      return mat;
    };

    if (Array.isArray(obj.material)) {
      obj.material = obj.material.map(applyMat);
    } else {
      obj.material = applyMat(obj.material);
    }

    if (isFaceDecal) {
      obj.renderOrder = 1;
      pokeableMeshes.push(obj);
      return;
    }
    addOutline(obj);
    pokeableMeshes.push(obj);
  });

  // センタリング & スケール
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const scale = 1.8 / Math.max(size.x, size.y, size.z);
  model.scale.setScalar(scale);
  model.userData.baseScale = scale; // bounboun スカッシュ用に保存
  model.position.x -= center.x * scale;
  model.position.z -= center.z * scale;
  model.position.y -= box.min.y * scale;

  // 前向きに180度回転
  model.rotation.y = Math.PI;

  scene.add(model);

  // ボーン収集 & スプリング初期化
  for (const name of BONE_NAMES) {
    bones[name] = findBone(model, name);
    if (bones[name]) {
      bones[name].matrixAutoUpdate = true;
      bones[name].userData.initRot   = bones[name].rotation.clone();
      bones[name].userData.initScale = bones[name].scale.clone();
      springs[name] = new BoneSpring(
        name.includes('しっぽ') ? 14 : name.includes('耳') ? 28 : 20,
        name.includes('しっぽ') ? 3.5 : name.includes('耳') ? 6 : 5,
      );
    }
  }

  document.getElementById('loading').classList.add('hidden');
  setTimeout(() => document.getElementById('bubble').classList.add('visible'), 400);

}, undefined, err => {
  console.error(err);
  document.querySelector('#loading span').textContent = 'Load failed';
});

// ─── Drag (もちもち引っ張り) + Tap (ぽうん) ──────────────────────────────
const raycaster = new THREE.Raycaster();
const mouse2d   = new THREE.Vector2();

// もちもちドラッグ対象ボーン: 首・頭を中心に体も少し追従
const MOCHI_BONES = {
  '首': { rx: 1.2, ry: 1.0, rz: 0.5 },
  '頭': { rx: 0.9, ry: 0.8, rz: 0.4 },
  '顔': { rx: 0.6, ry: 0.5, rz: 0.2 },
  'お尻': { rx: 0.3, ry: 0.1, rz: 0.1 },
  'お腹': { rx: 0.2, ry: 0.1, rz: 0.1 },
};

let dragState = null; // { prevX, prevY, onModel: bool }

function getNDC(e) {
  return {
    x:  (e.clientX / window.innerWidth)  * 2 - 1,
    y: -(e.clientY / window.innerHeight) * 2 + 1,
  };
}

renderer.domElement.addEventListener('pointerdown', e => {
  if (!model) return;
  const ndc = getNDC(e);
  mouse2d.set(ndc.x, ndc.y);
  raycaster.setFromCamera(mouse2d, camera);
  const hits = raycaster.intersectObjects(pokeableMeshes, false);

  if (hits.length > 0) {
    // モデルヒット → もちもちモード、OrbitControls無効
    controls.enabled = false;
    dragState = { prevX: e.clientX, prevY: e.clientY, onModel: true, moved: false };
    e.preventDefault();
  } else {
    dragState = { prevX: e.clientX, prevY: e.clientY, onModel: false, moved: false };
  }
});

renderer.domElement.addEventListener('pointermove', e => {
  if (!dragState || !dragState.onModel) return;
  e.preventDefault();

  const dx = (e.clientX - dragState.prevX) / window.innerWidth;
  const dy = (e.clientY - dragState.prevY) / window.innerHeight;
  dragState.prevX = e.clientX;
  dragState.prevY = e.clientY;
  dragState.moved = true;

  // 画面上の移動量 → ボーン回転ターゲットに加算
  const scale = 3.5;
  for (const [name, w] of Object.entries(MOCHI_BONES)) {
    const sp = springs[name];
    if (!sp) continue;
    sp.setTarget(
      Math.max(-0.7, Math.min(0.7, sp.tx - dy * scale * w.rx)),
      Math.max(-0.7, Math.min(0.7, sp.tz + dx * scale * w.rz)),
      Math.max(-0.7, Math.min(0.7, sp.ty + dx * scale * w.ry)),
    );
  }
}, { passive: false });

renderer.domElement.addEventListener('pointerup', e => {
  if (!dragState) return;

  if (dragState.onModel && !dragState.moved && model) {
    // タップ（移動なし）→ ぽうんポーク
    const ndc = getNDC(e);
    mouse2d.set(ndc.x, ndc.y);
    raycaster.setFromCamera(mouse2d, camera);
    const hits = raycaster.intersectObjects(pokeableMeshes, false);
    if (hits.length > 0) {
      spawnBurst(hits[0].point);
      for (const name of BONE_NAMES) {
        const bone = bones[name]; const sp = springs[name];
        if (!bone || !sp) continue;
        const bw = new THREE.Vector3();
        bone.getWorldPosition(bw);
        const str = Math.max(0, 1 - bw.distanceTo(hits[0].point) / 0.6);
        if (str < 0.01) continue;
        const dir = new THREE.Vector3().subVectors(bw, hits[0].point).normalize();
        sp.impulse(dir.y * str * 0.5, dir.x * str * 0.4, 0);
      }
    }
  }

  if (dragState.onModel) {
    // ターゲットを解放 → バネで戻る
    for (const name of Object.keys(MOCHI_BONES)) {
      springs[name]?.clearTarget();
    }
    controls.enabled = true;
  }

  dragState = null;
});

// ─── アニメ切替ボタン ───────────────────────────────────────────────────────
document.querySelectorAll('#anim-bar button').forEach(btn => {
  btn.addEventListener('click', () => {
    const name = btn.dataset.anim;
    if (name === currentAnim) return;
    currentAnim = name;
    animTime = 0;
    if (model) model.rotation.x = 0;
    document.querySelectorAll('#anim-bar button').forEach(b => b.classList.toggle('active', b === btn));

    const messages = {
      utouto: 'ふわぁ……ちょっとだけ、うとうとするにゃ',
      bounboun: 'ぽんぽん、はずむにゃ！',
      futaShime: 'くるんっと、ふたをしめるにゃ！',
      moimoi: 'もいもい、ゆらゆらにゃ',
      onedari: 'おねだりしてみるにゃ……！',
    };
    const bubble = document.getElementById('bubble');
    bubble.textContent = messages[name] ?? '';
    bubble.classList.remove('visible');
    requestAnimationFrame(() => bubble.classList.add('visible'));
  });
});

// ─── Animation Loop ────────────────────────────────────────────────────────
function animate() {
  requestAnimationFrame(animate);
  const dt = Math.min(clock.getDelta(), 0.05);
  const t  = clock.elapsedTime;

  // パーティクル上昇
  const pa = pGeo.attributes.position;
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    pa.array[i*3+1] += pSpeeds[i] * 0.007;
    if (pa.array[i*3+1] > 2.8) pa.array[i*3+1] = 0;
  }
  pa.needsUpdate = true;

  ring.material.opacity = 0.1 + Math.sin(t * 1.5) * 0.05;
  updateBursts(dt);

  if (model) {
    // ── スプリング更新 ───────────────────────────────────────────────────
    for (const name of BONE_NAMES) {
      springs[name]?.update(dt);
    }

    const breathe    = Math.sin(t * 1.1) * 0.5 + 0.5;
    const breatheSin = Math.sin(t * 0.55);

    // ── 各ボーン: ベースアニメ + スプリング加算 ──────────────────────────
    // (scale変更はSkinnedMeshに効かないのでrotationのみ使用)
    function applyBone(name, baseRx, baseRz, baseRy = 0) {
      const bone = bones[name]; const sp = springs[name];
      if (!bone) return;
      bone.rotation.x = bone.userData.initRot.x + baseRx + (sp?.rx ?? 0);
      bone.rotation.z = bone.userData.initRot.z + baseRz + (sp?.rz ?? 0);
      bone.rotation.y = bone.userData.initRot.y + baseRy  + (sp?.ry ?? 0);
      bone.updateMatrix();
    }

    // しっぽ・耳: 全アニメ共通
    applyBone('しっぽ根本', 0, Math.sin(t*0.9)*0.22);
    applyBone('しっぽ中',   0, Math.sin(t*0.9+0.5)*0.18);
    applyBone('しっぽ先',   0, Math.sin(t*0.9+1.0)*0.14);
    applyBone('耳.L',    0,  Math.sin(t*2.3)*0.09);
    applyBone('耳先.L',  0,  Math.sin(t*2.3+0.2)*0.07);
    applyBone('耳.R',    0, -Math.sin(t*2.3+0.7)*0.09);
    applyBone('耳先.R',  0, -Math.sin(t*2.3+0.9)*0.07);

    // 呼吸・肩: moimoi/onedari/utouto以外は共通 (それらは自前で上書き)
    if (currentAnim !== 'moimoi' && currentAnim !== 'onedari' && currentAnim !== 'utouto') {
      applyBone('お尻',  breatheSin *  0.04, 0);
      applyBone('お腹',  breatheSin * -0.03, 0);
      applyBone('首',    breatheSin *  0.10, 0);
      applyBone('頭',    breatheSin *  0.12, 0, Math.sin(t * 0.4) * 0.04);
      applyBone('顔',    breatheSin *  0.06, 0);
      applyBone('肩.L',  0,  Math.sin(t*0.7)*0.06);
      applyBone('肩.R',  0, -Math.sin(t*0.7+0.5)*0.06);
    }

    if (model.userData.baseY === undefined) model.userData.baseY = model.position.y;

    animTime += dt;
    const baseS = model.userData.baseScale ?? 1;

    // ── アニメーション分岐 ──────────────────────────────────────────────────
    if (currentAnim === 'utouto') {
      // 眠気の山を 0 → 1 → 0 で作り、開始・終了姿勢を完全に一致させる。
      const ut = (animTime % UTOUTO_DURATION) / UTOUTO_DURATION;
      const phase = ut * Math.PI * 2;
      const drowse = (1 - Math.cos(phase)) * 0.5;
      const sleepyBreath = Math.sin(phase * 2);
      const sway = Math.sin(phase) * 0.025;

      model.position.y = model.userData.baseY - drowse * 0.025 + sleepyBreath * 0.006;
      model.scale.x = baseS * (1 - sleepyBreath * 0.012);
      model.scale.y = baseS * (1 + sleepyBreath * 0.018);
      model.rotation.x = 0;

      // 閉眼は眠気の頂点を長めに保ち、首が戻り始める直前に開眼する。
      const showSleepyFace = ut >= 0.28 && ut < 0.72;
      egaoObjects.forEach(o => { o.visible =  showSleepyFace; });
      futsuuObjects.forEach(o => { o.visible = !showSleepyFace; });

      applyBone('お尻', sleepyBreath * 0.025, sway * 0.35);
      applyBone('お腹', sleepyBreath * -0.035 + drowse * 0.025, sway * 0.45);
      applyBone('首',   0.03 + drowse * 0.14 + sleepyBreath * 0.025, sway * 0.4);
      applyBone('頭',   0.04 + drowse * 0.22 + sleepyBreath * 0.035, sway * 0.7, sway * 0.5);
      applyBone('顔',   0.02 + drowse * 0.10, sway * 0.35);
      applyBone('肩.L', drowse * 0.025,  0.05 + sleepyBreath * 0.025);
      applyBone('肩.R', drowse * 0.025, -0.05 - sleepyBreath * 0.025);

      // 眠るほど耳が外側へほどけ、しっぽはゆっくり一往復する。
      applyBone('耳.L',   0,  0.04 + drowse * 0.13 + Math.sin(phase * 2 + 0.3) * 0.018);
      applyBone('耳先.L', 0,  0.03 + drowse * 0.09);
      applyBone('耳.R',   0, -0.04 - drowse * 0.13 - Math.sin(phase * 2 + 0.3) * 0.018);
      applyBone('耳先.R', 0, -0.03 - drowse * 0.09);
      applyBone('しっぽ根本', 0, Math.sin(phase) * 0.18);
      applyBone('しっぽ中',   0, Math.sin(phase + 0.45) * 0.15);
      applyBone('しっぽ先',   0, Math.sin(phase + 0.9) * 0.12);

    } else if (currentAnim === 'bounboun') {
      const bt = animTime % BB_DURATION;
      const bbY  = hermiteInterp(BB_POS_Y,   bt);
      const bbSX = hermiteInterp(BB_SCALE_X, bt);
      const bbSY = hermiteInterp(BB_SCALE_Y, bt);
      const yOffset = (bbY - 2.0) * 0.28;
      model.position.y = model.userData.baseY + yOffset + Math.sin(t*0.8)*0.012;
      model.scale.x = baseS * (bbSX / 0.5);
      model.scale.y = baseS * (bbSY / 0.5);
      model.rotation.x = 0;
      const showEgao = bt >= BB_EGAO_START && bt < BB_EGAO_END;
      egaoObjects.forEach(o => { o.visible =  showEgao; });
      futsuuObjects.forEach(o => { o.visible = !showEgao; });

    } else if (currentAnim === 'futaShime') {
      const ft = animTime % FS_DURATION;
      const fsY  = hermiteInterp(FS_POS_Y,   ft);
      const fsSX = hermiteInterp(FS_SCALE_X, ft);
      const fsSY = hermiteInterp(FS_SCALE_Y, ft);
      const fsRX = hermiteInterp(FS_ROT_X,   ft);
      const yOffset = (fsY - 2.68) * 0.28;
      model.position.y = model.userData.baseY + yOffset;
      model.scale.x = baseS * (fsSX / 0.5);
      model.scale.y = baseS * (fsSY / 0.5);
      model.rotation.x = fsRX * DEG2RAD;
      // 表情: 最初(0〜1s)と末尾スピン(1.45s〜)は >< 、落下中はどちらも非表示
      const fsShowEgao = ft < 1.0 || ft >= 1.45;
      egaoObjects.forEach(o => { o.visible =  fsShowEgao; });
      futsuuObjects.forEach(o => { o.visible = false; });

    } else if (currentAnim === 'moimoi') {
      // 肩 Y 振り (Unity: 3°↔50° = 中心26.5°, 振幅23.5°, 周期0.6667s)
      const moiPhase = 2 * Math.PI * animTime / 0.6667;
      const moiShoulderY = 23.5 * DEG2RAD * Math.cos(moiPhase);
      // スケールスカッシュ (0.4↔0.5, 同周期)
      const moiScaleY = 0.45 + 0.05 * Math.cos(moiPhase);
      model.position.y = model.userData.baseY + Math.sin(t*0.8)*0.006;
      model.scale.x = baseS;
      model.scale.y = baseS * (moiScaleY / 0.5);
      model.rotation.x = 0;
      egaoObjects.forEach(o => { o.visible = false; });
      futsuuObjects.forEach(o => { o.visible = true; });
      // 肩ボーンへオーバーライド (applyBone の4番目引数=baseRy)
      const moiSway = 0.06 * Math.sin(moiPhase); // 体の横揺れ
      applyBone('お尻',  breatheSin * 0.03 + moiSway * 0.4, 0);
      applyBone('お腹',  breatheSin * -0.02 + moiSway * 0.3, 0);
      applyBone('首',    breatheSin * 0.08, 0, moiSway * 0.2);
      applyBone('頭',    breatheSin * 0.10, 0, moiSway * 0.15 + Math.sin(t*0.4)*0.04);
      applyBone('顔',    breatheSin * 0.05, 0, moiSway * 0.1);
      applyBone('肩.R', 0, Math.sin(t*0.7)*0.04,  moiShoulderY);
      applyBone('肩.L', 0, -Math.sin(t*0.7+0.5)*0.04, moiShoulderY);

    } else if (currentAnim === 'onedari') {
      // おねだり: 0.6667s ループ, 肩 Y 振り + スカッシュ + 前傾き
      const odPhase = 2 * Math.PI * animTime / 0.6667;
      const odShoulderY = 23.5 * DEG2RAD * Math.cos(odPhase);
      const odScaleY = 0.45 + 0.05 * Math.cos(odPhase);
      // 前後に小さく揺れる (おねだり感)
      const odNod = 0.07 + 0.04 * Math.cos(odPhase); // 前傾き固定+揺れ
      model.position.y = model.userData.baseY + Math.sin(t*0.8)*0.005;
      model.scale.x = baseS;
      model.scale.y = baseS * (odScaleY / 0.5);
      model.rotation.x = 0;
      egaoObjects.forEach(o => { o.visible = false; });
      futsuuObjects.forEach(o => { o.visible = true; });
      applyBone('お尻',  breatheSin * 0.03, 0);
      applyBone('お腹',  breatheSin * -0.02 + odNod * 0.3, 0);
      applyBone('首',    breatheSin * 0.08 + odNod * 0.6, 0);
      applyBone('頭',    breatheSin * 0.10 + odNod * 0.5, 0, Math.sin(t*0.4)*0.03);
      applyBone('顔',    breatheSin * 0.05 + odNod * 0.3, 0);
      applyBone('肩.R', 0, Math.sin(t*0.7)*0.04,  odShoulderY);
      applyBone('肩.L', 0, -Math.sin(t*0.7+0.5)*0.04, odShoulderY);
    }
  }

  controls.update();
  composer.render();
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  updateCameraDistance();
  renderer.setSize(window.innerWidth, window.innerHeight);
  composer.setSize(window.innerWidth, window.innerHeight);
  bloomPass.resolution.set(window.innerWidth, window.innerHeight);
});

animate();
